import pluralize from "pluralize";
import { format } from "date-fns";
import downloadFile from "js-file-download";
import { toast } from "react-semantic-toasts";
import { isEmpty } from "lodash";

import { makePrivateApiCall } from "utilities/dist/api";
import { FilenameDateFormat } from "utilities/dist/invoice/constants";
import { trackDownloadPdfEvent } from "../common/analytics";
import { isIos } from "api/src/common/helpers";
import {
    buildInvoiceNumberWithBeer,
    computeTaxedTotal,
    computeSubTotal,
} from "utilities/dist/invoice/helpers";
import { formatAmount } from "utilities/dist/helpers";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { InvoiceConverterFintech } from "utilities/dist/invoice/converter/fintech";
import { isBeer, isLiquor, isWine, isNa } from "utilities/dist/helpers";

const showStateTax = item => {
    const itemPrice = item.price * item.qty;
    const totalTax = item.total - itemPrice;
    return formatAmount(totalTax / item.qty).replace("$", "");
};

export const computeTotalFromItems = (items, tax_rate) => {
    let subTotal = 0,
        taxTotal = 0,
        total = 0,
        totalItems = 0,
        totalUnits = 0,
        beerItem = 0,
        naItem = 0,
        liquorItem = 0,
        positiveLiquorCount = 0,
        wineItem = 0,
        beerCount = 0,
        naCount = 0,
        liquorCount = 0,
        wineCount = 0;

    items.forEach(item => {
        // item total includes the tax but since in the summary, we're showing the split-up calculation
        // we need to use the total without tax for next computations
        const totalWithoutTax = computeSubTotal(item);
        const tax = computeTaxedTotal({ ...item, total: totalWithoutTax }, tax_rate);

        subTotal += totalWithoutTax;
        taxTotal += tax;
        total += item.total;
        totalItems += 1;
        totalUnits += item.qty;

        if (isBeer(item)) {
            beerCount += item.qty;
            beerItem += 1;
        } else if (isLiquor(item)) {
            liquorCount += item.qty;
            liquorItem += 1;
            if (item.qty > 0) positiveLiquorCount += item.qty;
        } else if (isWine(item)) {
            wineCount += item.qty;
            wineItem += 1;
        } else if (isNa(item)) {
            naCount += item.qty;
            naItem += 1;
        }
    });

    const counts = { subTotal, taxTotal, total, totalItems, totalUnits };

    if (beerItem > 0) {
        counts.beerCount = beerCount;
        counts.beerItem = beerItem;
    }

    if (liquorItem > 0) {
        counts.liquorCount = liquorCount;
        counts.liquorItem = liquorItem;
        counts.positiveLiquorCount = positiveLiquorCount;
    }

    if (wineItem > 0) {
        counts.wineCount = wineCount;
        counts.wineItem = wineItem;
    }

    if (naItem > 0) {
        counts.naCount = naCount;
        counts.naItem = naItem;
    }

    return counts;
};

export const getNewInvoiceNumber = customerId =>
    makePrivateApiCall({
        url: `/invoice/new-number?customerId=${customerId}`,
    }).then(res => res.data);

/**
 * Given an invoice entry, it computes the total amount of all the payment entries added to the invoice
 * @function computePaymentTotal
 *
 * @param {Object} invoice invoice data
 *
 * @returns {Number} Total summation of all the payment entries logged
 */
export const computePaymentTotal = invoice => {
    if (!invoice || isEmpty(invoice.payments)) {
        return 0;
    }

    return invoice.payments.reduce((total, payment) => {
        return total + Number(payment.amount);
    }, 0);
};

/**
 * Finds out how many stamps are being used in an invoice by looping through all serialized stamps
 * Used in the invoice builder reducer and the summary component
 */
export const computeStampTotal = (stamps = []) => {
    const total = stamps.reduce((previousTotal, { from, to }) => {
        return to - from + previousTotal + 1;
    }, 0);

    return total;
};

/**
 * Liquor stamp requirement minus stamps already assigned as ranges.
 * Positive = more stamps needed; negative = excess stamps (see summary warning).
 */
export const computeMissingStamps = (liquorStampRequirement, stamps = []) =>
    liquorStampRequirement - computeStampTotal(stamps);

/**
 * Finds out how many stamps are needed by counting total number of liquor bottles
 * Used in the invoice builder reducer
 */
export const countStampsNeeded = ({ items = {} }) => {
    const liquorBottleCount = Object.values(items).reduce((total, item) => {
        if (isLiquor(item) && item.qty > 0) return item.qty + total;

        return total;
    }, 0);

    return liquorBottleCount;
};

export const DateFormat = "dd MMM, yyyy";
export const DateFormatForTxt = "MM/dd/yyyy";
export const isQtyComputable = qty => qty !== "";

/**
 * @function generatePdf
 *
 * @param {String} number invoice number
 * @param {Function} setGeneratingPdfUrl Function that will be called to set the sate of generation process, true will be passed when the process starts and false will be passed when the process ends un/successfully
 * @param {Boolean} shouldDownload if invoice should be immediately downloaded
 */
export const generatePdf = (number, setGeneratingPdfUrl, shouldDownload = true) => {
    trackDownloadPdfEvent(number, {});
    setGeneratingPdfUrl(true);

    return makePrivateApiCall({
        method: "POST",
        url: "/invoice/pdf",
        data: { number },
        // only when download is needed, we need the file as blob, otherwise json /default is fine
        ...(shouldDownload ? { responseType: "blob" } : {}),
    })
        .then(res => {
            trackDownloadPdfEvent(number, { success: true });
            setGeneratingPdfUrl(false);
            if (shouldDownload) {
                if (isIos()) {
                    const file = new Blob([res.data], { type: "application/pdf" });
                    const fileURL = URL.createObjectURL(file);
                    return window.open(fileURL, "_blank");
                }
                return downloadFile(res.data, `inv_${number}.pdf`);
            }
            // when not downloading, data will contain url
            return res.data?.url;
        })
        .catch(err => {
            trackDownloadPdfEvent(number, { error: true });
            setGeneratingPdfUrl(false);
            toast({
                icon: "file",
                type: "error",
                title: "Error generating pdf",
                description: `${err.message}`,
            });
        });
};

export const generatePdfLocations = (number, setGeneratingPdfUrl, shouldDownload = true) => {
    trackDownloadPdfEvent(number, {});
    setGeneratingPdfUrl(true);

    return makePrivateApiCall({
        method: "POST",
        url: "/invoice/pdf",
        data: { number, shouldIncludeLocations: true },
        // only when download is needed, we need the file as blob, otherwise json /default is fine
        ...(shouldDownload ? { responseType: "blob" } : {}),
    })
        .then(res => {
            trackDownloadPdfEvent(number, { success: true });
            setGeneratingPdfUrl(false);
            if (shouldDownload) {
                if (isIos()) {
                    const file = new Blob([res.data], { type: "application/pdf" });
                    const fileURL = URL.createObjectURL(file);
                    return window.open(fileURL, "_blank");
                }
                return downloadFile(res.data, `loc_inv_${number}.pdf`);
            }
            // when not downloading, data will contain url
            return res.data?.url;
        })
        .catch(err => {
            trackDownloadPdfEvent(number, { error: true });
            setGeneratingPdfUrl(false);
            toast({
                icon: "file",
                type: "error",
                title: "Error generating pdf",
                description: `${err.message}`,
            });
        });
};

export const getProductFullName = (product = {}) => `${product.product} (${product.product_size})`;

// TODO: Change the func def

export const formatItemWithUnits = ({ data: invoice }) => {
    if (!invoice) return null;
    const qtyCount = invoice.items.reduce((count, { qty }) => count + qty, 0);
    return `${pluralize("unit", qtyCount, true)} of ${pluralize(
        "item",
        invoice.items.length,
        true
    )}`;
};

const buildDetailedRows = (invoice, i, { isCustomerInvoiceNumberingActive }) => {
    const rows = [];
    const invoiceColumns = {
        invoice_status: invoice.status || "", // empty if no value found
        invoice_number: buildInvoiceNumberWithBeer(invoice, isCustomerInvoiceNumberingActive) || "",
        customer_name: invoice.customer?.name || "",
        fintech_id: invoice.customer.fintech_id || "",
        customer_email: invoice.customer?.contact_email || "",
        address: invoice.customer?.address || "",
        city: invoice.customer?.city || "",
        state: invoice.customer?.state || "",
        zip: invoice.customer?.zip || "",
        phone_number: invoice.customer?.contact_phone || "",
        cod: invoice.customer.cash_on_delivery ? "Yes" : "No",
    };

    invoice.items.forEach(item => {
        const itemColumn = {
            product_name: item.product?.product || "",
            product_type: item.product?.product_type || "",
            upc: item.product?.upc || "",
            last_purchase_price: formatAmount(item.product?.last_purchase_price) || "",
            unit_price: formatAmount(item.price) || "",
            quantity: item.qty || "",
            state_tax: item.taxable ? showStateTax(item) : 0,
            total: formatAmount(item.total) || "",
        };

        rows.push({ ...invoiceColumns, ...itemColumn });
    });

    return rows;
};
const buildSummaryRows = (invoice, i, { isCustomerInvoiceNumberingActive }) => {
    const rows = [
        {
            invoice_status: invoice.status || "", // empty if no value found
            invoice_number:
                buildInvoiceNumberWithBeer(invoice, isCustomerInvoiceNumberingActive) || "",
            total: formatAmount(invoice.total) || "",
            tax: formatAmount(invoice.tax_total) || 0,
            payment_method: [...new Set(invoice.payments?.map(p => p.payment_method))]
                .sort()
                .join(","),
            date: invoice.invoiced_at || "",
            due_on: invoice.due_at || "",
            customer: invoice.customer?.name || "",
            fintech_id: invoice.customer.fintech_id || "",
            items: formatItemWithUnits({ data: invoice }) || "",
            cod: invoice.customer.cash_on_delivery ? "Yes" : "No",
        },
    ];

    return rows;
};

export const handleExportInvoice = (invoices, company, exportType) => {
    if (!invoices?.length) return null;
    const rows = [];
    const buildRows = exportType === "summary" ? buildSummaryRows : buildDetailedRows;
    const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
        company,
        CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
    );
    // structure data with each row for one items
    // invoice_status invoice_number customer_name customer_email address city state zip phone_number product_name unit_price buying_price quantity total
    invoices.forEach((invoice, i) => {
        const invoiceRows = buildRows(invoice, i, { isCustomerInvoiceNumberingActive });
        if (i === 0) {
            const headers = Object.keys(invoiceRows[0]).map(key =>
                key.toLowerCase().replaceAll("_", " ")
            );
            rows.push(`"${headers.join('","')}"`);
        }

        invoiceRows.forEach(invoiceRow => {
            let columnValues = Object.values(invoiceRow);

            const parsedRow = columnValues.join('","');
            rows.push(`"${parsedRow}"`);
        });
    });

    const aTag = document.createElement("a");
    const textFile = new Blob([rows.join("\r\n").toLocaleUpperCase()], {
        type: "text/csv;charset=utf-8;",
    });
    aTag.href = URL.createObjectURL(textFile);
    aTag.download = `invoices_${exportType === "summary" ? "summary" : "detailed"}_${format(
        new Date(),
        FilenameDateFormat
    )}${".csv"}`;
    aTag.click();
};

export const handleFintechCsvExport = (invoices, company, filename) => {
    if (!invoices?.length) return null;
    const converter = new InvoiceConverterFintech({ invoices, company });
    const rows = converter.getRows();
    const aTag = document.createElement("a");
    const textFile = new Blob([rows], {
        type: "text/csv;charset=utf-8;",
    });
    aTag.href = URL.createObjectURL(textFile);
    aTag.download =
        filename ?? `invoices_fintech_${format(new Date(), FilenameDateFormat)}${".csv"}`;
    aTag.click();
};

export const createItemLog = items => {
    return (
        items.map(item => ({
            ...item,
            product: item.product ? item.product : item.ydk_product,
            isRejected: false,
            rejectionNote: "",
            previousPrice: item.price,
            newPrice: "",
            priceChangeNote: "",
            previousQty: item.qty,
            newQty: "",
            qtyChangeNote: "",
        })) || []
    );
};

export const handleBottlePosJsonExport = filterModel => {
    makePrivateApiCall({
        url: "/bottle-pos/export-invoices",
        method: "GET",
        params: { filterModel },
    })
        .then(res => {
            if (!res.data) {
                return toast({
                    description: `Error exporting bottle pos sales report`,
                    time: 30000,
                    type: "error",
                    icon: "warning sign",
                    title: "Error exporting",
                });
            }

            const aTag = document.createElement("a");
            const jsonFile = new Blob([JSON.stringify(res.data)], {
                type: "application/json;charset=utf-8;",
            });
            aTag.href = URL.createObjectURL(jsonFile);
            aTag.download = `sales_import_bottle_pos_${format(
                new Date(),
                FilenameDateFormat
            )}${".json"}`;
            aTag.click();
        })
        .catch(err => {
            toast({
                description: err.message,
                time: 30000,
                type: "error",
                icon: "warning sign",
                title: "Error exporting",
            });
        });
};

export const getFileName = fileName => {
    if (!fileName) return "";

    const fragments = fileName.split(".");
    fragments.pop();
    const name = fragments.join(".");

    return name;
};

export const getFileExtension = fileName => {
    if (!fileName) return "";

    const fragments = fileName.split(".");
    const fileExtension = fragments.pop();

    return fileExtension;
};
